import React, { useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { literalTextToRegex } from '../../../util/string-convert';
import { StoreState } from '../../../store/store';
import { createNewAeTitleRule, generateNewId, createNewDicomAttributeRule, createNewDicomRule } from '../../../store/global-types/customization-helpers';
import { ModelType } from '../../../store/global-types/customization-types';
import { aeTitleRuleAdded, doseSelectors, dicomAttributeRuleAdded, dicomRuleAdded } from '../../../store/dose/doseSlice';
import { ModelSelectionUndoState } from '../ModelSelectionPage';
import SelectionRulesForm from '../SelectionRulesForm';
import DoseAeTitleRule from './DoseAeTitleRule';
import DoseDicomAttributeRule from './DoseDicomAttributeRule';
import DoseDicomRule from './DoseDicomRule';

interface DoseSelectionRulesProps {
    customizationBaseId: string,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    heading: string,
    hideCustomizationName?: boolean,
    showMultipleOutputsWarning: boolean,
    /** If true, don't show undo UI options. */
    isUndoDisabled?: boolean,
}

const DoseSelectionRules = (props: DoseSelectionRulesProps) => {
    const { customizationBaseId, setUndoState, heading, hideCustomizationName, showMultipleOutputsWarning, isUndoDisabled } = props;

    const dispatch = useDispatch();

    const customizationBase = useSelector((state: StoreState) => doseSelectors.selectCustomizationBaseById(state, customizationBaseId));
    const model = useSelector((state: StoreState) => customizationBase && doseSelectors.selectModelById(state, customizationBase.modelId));
    const activeTags = useSelector((state: StoreState) => state.dose.activeTagFilters);
    const activeVersionTags = useSelector((state: StoreState) => state.dose.activeVersionTags);
    const activeInputModalities = useSelector((state: StoreState) => doseSelectors.selectActiveInputModalities(state));
    const activeOutputModalities = useSelector((state: StoreState) => doseSelectors.selectActiveOutputModalities(state));

    const handleAddAeTitleRuleClick = useCallback(() => {
        const newAeTitleRule = createNewAeTitleRule(undefined, null, undefined, undefined,
            undefined, customizationBaseId, true);
        dispatch(aeTitleRuleAdded(newAeTitleRule));
    }, [dispatch, customizationBaseId]);

    const handleAddDicomRuleClick = useCallback(() => {
        const dicomRuleId = generateNewId();
        const newDicomAttribute = createNewDicomAttributeRule('', literalTextToRegex(''), dicomRuleId, true);
        const newDicomRule = createNewDicomRule([newDicomAttribute.id], dicomRuleId,
            customizationBaseId, null, undefined, true);
        dispatch(dicomRuleAdded(newDicomRule));
        dispatch(dicomAttributeRuleAdded(newDicomAttribute));
    }, [dispatch, customizationBaseId]);


    const getDicomAttributeRule = useCallback((id: string, runningNumber: number, onRemoveDicomRuleButtonClicked: () => void) => {
        return (<DoseDicomAttributeRule key={id}
            dicomAttributeRuleId={id} runningNumber={runningNumber}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
            onRemoveDicomRule={onRemoveDicomRuleButtonClicked}
        />)
    }, [setUndoState, isUndoDisabled]);


    const getAeTitleRule = useCallback((id: string, runningNumber: number, isLastRule: boolean) => {
        return (<DoseAeTitleRule key={id}
            aeTitleRuleId={id} runningNumber={runningNumber} isLastRule={isLastRule}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
        />);
    }, [setUndoState, isUndoDisabled]);

    
    const getDicomRule = useCallback((id: string, runningNumber: number, isLastRule: boolean) => {
        return (<DoseDicomRule key={id}
            dicomRuleId={id} runningNumber={runningNumber} isLastRule={isLastRule}
            setUndoState={setUndoState} isUndoDisabled={isUndoDisabled}
            getDicomAttributeRule={getDicomAttributeRule}
        />);
    }, [setUndoState, isUndoDisabled, getDicomAttributeRule]);


    if (customizationBase === undefined || model === undefined) {
        return null;
    }

    return (
        <SelectionRulesForm
            modelType={ModelType.Dose}
            aeTitleRuleIds={customizationBase.aeTitleRules}
            dicomRuleIds={customizationBase.dicomRules}
            modelName={model.modelName}
            modelLabel={model.label}
            customizationName={customizationBase.customizationName}
            isCustomizationBaseModified={customizationBase.isModified}
            onAeTitleRuleAdded={handleAddAeTitleRuleClick}
            onDicomRuleAdded={handleAddDicomRuleClick}
            setUndoState={setUndoState}
            heading={heading}
            hideCustomizationName={hideCustomizationName}
            showMultipleOutputsWarning={showMultipleOutputsWarning}
            isUndoDisabled={isUndoDisabled}
            getAeTitleRule={getAeTitleRule}
            getDicomRule={getDicomRule}
            isDicomSelectionDisabled={true}
            activeTags={activeTags}
            activeVersionTags={activeVersionTags}
            activeInputModalities={activeInputModalities}
            activeOutputModalities={activeOutputModalities}
        />

    );
}

// Custom shallow comparison function for props
function arePropsEqual(prevProps: DoseSelectionRulesProps, nextProps: DoseSelectionRulesProps) {
    return (
        prevProps.customizationBaseId === nextProps.customizationBaseId &&
        prevProps.setUndoState === nextProps.setUndoState &&
        prevProps.heading === nextProps.heading &&
        prevProps.hideCustomizationName === nextProps.hideCustomizationName &&
        prevProps.showMultipleOutputsWarning === nextProps.showMultipleOutputsWarning &&
        prevProps.isUndoDisabled === nextProps.isUndoDisabled
    );
}

export default memo(DoseSelectionRules, arePropsEqual);
